import { useSetAtom } from 'jotai'
import { ReactNode, useEffect, useMemo, useState } from 'react'

import { BalancesRpcCallUpdater } from '@cowprotocol/balances-and-allowances'
import { SWR_NO_REFRESH_OPTIONS } from '@cowprotocol/common-const'
import type { SupportedChainId } from '@cowprotocol/cow-sdk'
import { LP_TOKEN_LIST_CATEGORIES, useAllLpTokens } from '@cowprotocol/tokens'

import ms from 'ms.macro'

import { areLpBalancesLoadedAtom } from './lpBalancesState'

// A small gap between balances and allowances refresh intervals is needed to avoid high load to the node at the same time
const LP_BALANCES_SWR_CONFIG = { refreshInterval: ms`32s`, revalidateIfStale: false }
const LP_MULTICALL_OPTIONS = { consequentExecution: true }

// To avoid high load to the node at the same time
// We start the updater with a delay
const LP_UPDATER_START_DELAY = ms`3s`

export interface BalancesAndAllowancesUpdaterProps {
  account: string | undefined
  chainId: SupportedChainId
  enablePolling: boolean
}

export function LpBalancesAndAllowancesUpdater({
  account,
  chainId,
  enablePolling,
}: BalancesAndAllowancesUpdaterProps): ReactNode {
  const allLpTokens = useAllLpTokens(LP_TOKEN_LIST_CATEGORIES)
  const [isUpdaterPaused, setIsUpdaterPaused] = useState(true)
  const setAreLpBalancesLoaded = useSetAtom(areLpBalancesLoadedAtom)

  const lpTokenAddresses = useMemo(() => allLpTokens.map((token) => token.address), [allLpTokens])

  useEffect(() => {
    if (!enablePolling) return

    const timeout = setTimeout(() => {
      setIsUpdaterPaused(false)
    }, LP_UPDATER_START_DELAY)

    return () => clearTimeout(timeout)
  }, [enablePolling])

  useEffect(() => {
    setAreLpBalancesLoaded(false)
  }, [account, setAreLpBalancesLoaded])

  return (
    <>
      <BalancesRpcCallUpdater
        chainId={chainId}
        tokenAddresses={lpTokenAddresses}
        balancesSwrConfig={enablePolling ? LP_BALANCES_SWR_CONFIG : SWR_NO_REFRESH_OPTIONS}
        account={isUpdaterPaused ? undefined : account}
        setLoadingState={false}
        onBalancesLoaded={setAreLpBalancesLoaded}
        multicallOptions={LP_MULTICALL_OPTIONS}
      />
    </>
  )
}
